import { withAuthRecovery } from '@/lib/authRecovery'
import { getSupabase } from '@/lib/supabase'

export type MissingPaymentsIssueType =
  | 'quarantined'
  | 'missing'
  | 'deleted'
  | 'unattributed'
  | 'reissue'

export type MissingPaymentsControlSummaryRow = {
  issue_type: MissingPaymentsIssueType
  account: string
  studio_number: number | null
  studio_internal_id: number | null
  city_name: string | null
  address: string | null
  status_code: string | null
  partner_number: number | null
  persons_fio: string | null
  cities_text: string | null
  currency: string | null
  payment_count: number
  total_amount: number
  abs_amount: number
  oldest_paid_on: string | null
  newest_paid_on: string | null
  last_detected_at: string | null
  days_silent: number | null
  quarantine_id: number | null
  review_by: string | null
  review_overdue: boolean
  frozen_amounts: Record<string, number>
}

export type MissingPaymentsControlItem = {
  issue_type: MissingPaymentsIssueType
  account: string
  studio_number: number | null
  effective_studio_number: number | null
  payment_item_id: number | null
  payment_id: string
  paid_at: string
  amount: number
  currency: string
  payment_method: string | null
  added_by: string | null
  client_id: string | null
  client_name: string | null
  client_phone: string | null
  category: string | null
  basis: string | null
  subscription_id: string | null
  detected_at: string | null
  run_id: string | null
  reason: string
  old_state: Record<string, unknown> | null
  new_state: Record<string, unknown> | null
}

export type MissingPaymentsControlRun = {
  logical_run_id: string
  mode: string | null
  window_start: string
  window_end: string
  status: string
  row_count: number
  account_count: number
  date_count: number
  currency_sums: Record<string, number>
  preflight_status: string | null
  missing_accounts: Array<Record<string, unknown>>
  missing_pairs: Array<Record<string, unknown>>
  quarantined: Array<Record<string, unknown>>
  quarantine_released: Array<Record<string, unknown> | string>
  error_message: string | null
  started_at: string
  finished_at: string | null
  updated_at: string
}

/**
 * Класс CRM-аккаунта. `franchisee` — у аккаунта есть платежи с номерной студией,
 * поэтому деньги без номера у него означают «пробили не на тот филиал».
 * `no_numbered_studios` — служебный/тестовый аккаунт (Онлайн-тренировки, Test),
 * номерных студий у него нет вовсе.
 */
export type RevenueAccountClass = 'franchisee' | 'no_numbered_studios' | 'unknown'

export type RevenueAccountDirectoryRow = {
  account: string
  city_name: string | null
  country_name: string | null
  account_fio: string | null
  last_seen_on: string | null
  account_class: RevenueAccountClass
  numbered_studio_count: number
  numbered_payment_count: number
  blank_payment_count: number
  blank_abs_amount: number
}

export type ConfirmMissingPaymentDeletedResult = {
  payment_item_id: number
  payment_id: string
  paid_at: string
  amount: number
  currency: string
  studio_number: number | null
  deleted_at: string
  deleted_run_id: string | null
  rebuild_result: Record<string, unknown>
}

export async function fetchMissingPaymentsControlSummary(params: {
  from?: string
  to?: string
  detectedFrom?: string | null
  detectedTo?: string | null
  paidFrom?: string | null
  paidTo?: string | null
}): Promise<MissingPaymentsControlSummaryRow[]> {
  const paidFrom = params.paidFrom ?? (params.from ? params.from.slice(0, 10) : null)
  const paidTo = params.paidTo ?? (params.to ? params.to.slice(0, 10) : null)
  const detectedFrom = params.detectedFrom ?? params.from ?? null
  const detectedTo = params.detectedTo ?? params.to ?? null

  const v2 = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_revenue_source_control_summary_v2', {
      p_detected_from: detectedFrom,
      p_detected_to: detectedTo,
      p_paid_from: paidFrom,
      p_paid_to: paidTo,
    })
  )) as { data: MissingPaymentsControlSummaryRow[] | null; error: any }

  if (!v2.error) return v2.data ?? []

  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_revenue_source_control_summary', {
      p_from: params.from ?? params.detectedFrom ?? params.paidFrom,
      p_to: params.to ?? params.detectedTo ?? params.paidTo,
    })
  )) as { data: MissingPaymentsControlSummaryRow[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchMissingPaymentsControlItems(params: {
  from?: string
  to?: string
  detectedFrom?: string | null
  detectedTo?: string | null
  paidFrom?: string | null
  paidTo?: string | null
  issueType?: MissingPaymentsIssueType | null
  account?: string | null
  studioNumber?: number | null
  limit?: number
}): Promise<MissingPaymentsControlItem[]> {
  const paidFrom = params.paidFrom ?? (params.from ? params.from.slice(0, 10) : null)
  const paidTo = params.paidTo ?? (params.to ? params.to.slice(0, 10) : null)
  const detectedFrom = params.detectedFrom ?? params.from ?? null
  const detectedTo = params.detectedTo ?? params.to ?? null

  const v2 = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_revenue_source_control_items_v2', {
      p_detected_from: detectedFrom,
      p_detected_to: detectedTo,
      p_paid_from: paidFrom,
      p_paid_to: paidTo,
      p_issue_type: params.issueType ?? null,
      p_account: params.account ?? null,
      p_studio_number: params.studioNumber ?? null,
      p_limit: params.limit ?? 500,
      p_after_detected_at: null,
      p_after_payment_item_id: null,
    })
  )) as { data: MissingPaymentsControlItem[] | null; error: any }

  if (!v2.error) return v2.data ?? []

  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_revenue_source_control_items', {
      p_from: params.from ?? params.detectedFrom ?? params.paidFrom,
      p_to: params.to ?? params.detectedTo ?? params.paidTo,
      p_issue_type: params.issueType ?? null,
      p_account: params.account ?? null,
      p_studio_number: params.studioNumber ?? null,
      p_limit: params.limit ?? 500,
    })
  )) as { data: MissingPaymentsControlItem[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function fetchMissingPaymentsControlItemsPage(params: {
  detectedFrom?: string | null
  detectedTo?: string | null
  paidFrom?: string | null
  paidTo?: string | null
  issueType?: MissingPaymentsIssueType | null
  account?: string | null
  studioNumber?: number | null
  limit?: number
  afterDetectedAt?: string | null
  afterPaymentItemId?: number | null
}): Promise<{ rows: MissingPaymentsControlItem[]; hasMore: boolean }> {
  const limit = params.limit ?? 200
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_revenue_source_control_items_v2', {
      p_detected_from: params.detectedFrom ?? null,
      p_detected_to: params.detectedTo ?? null,
      p_paid_from: params.paidFrom ?? null,
      p_paid_to: params.paidTo ?? null,
      p_issue_type: params.issueType ?? null,
      p_account: params.account ?? null,
      p_studio_number: params.studioNumber ?? null,
      p_limit: limit + 1,
      p_after_detected_at: params.afterDetectedAt ?? null,
      p_after_payment_item_id: params.afterPaymentItemId ?? null,
    })
  )) as { data: MissingPaymentsControlItem[] | null; error: any }

  if (error) throw error
  const rows = data ?? []
  return { rows: rows.slice(0, limit), hasMore: rows.length > limit }
}

export async function fetchMissingPaymentsControlRuns(limit = 10): Promise<MissingPaymentsControlRun[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_revenue_source_control_runs', {
      p_limit: limit,
    })
  )) as { data: MissingPaymentsControlRun[] | null; error: any }

  if (error) throw error
  return data ?? []
}

/**
 * Подписи CRM-аккаунтов (город/ФИО из источника) + класс аккаунта.
 * Нужны там, где у строки нет студии: по студии подписать нечем, а по аккаунту —
 * есть. Атрибуцию платежа этот справочник не задаёт, только подпись для человека.
 */
export async function fetchRevenueAccountDirectory(): Promise<RevenueAccountDirectoryRow[]> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('get_revenue_account_directory')
  )) as { data: RevenueAccountDirectoryRow[] | null; error: any }

  if (error) throw error
  return data ?? []
}

export async function confirmMissingPaymentDeleted(params: {
  paymentItemId: number
  reason?: string | null
}): Promise<ConfirmMissingPaymentDeletedResult> {
  const { data, error } = (await withAuthRecovery(() =>
    (getSupabase() as any).rpc('confirm_revenue_missing_payment_deleted', {
      p_payment_item_id: params.paymentItemId,
      p_reason: params.reason ?? null,
    })
  )) as { data: ConfirmMissingPaymentDeletedResult[] | null; error: any }

  if (error) throw error
  const row = data?.[0]
  if (!row) throw new Error('Сервер не вернул подтверждение удаления')
  return row
}
